/**
 * MiniEdges Component
 *
 * Renders dependency edges on the minimap using the same routed SVG paths as the
 * main flowchart (edge.pathD from useEdgePathCache / focus layout). Maps world
 * coordinates into minimap space with the same inset as MiniNode tiles.
 */

import React, { useMemo, memo } from 'react';

/** Matches MiniNode inset so lines meet minimap tiles consistently */
const MINIMAP_NODE_SHRINK = 1;

const MiniEdges = memo(
  ({
    edges = [],
    nodePositions,
    nodeDimensions,
    scale,
    boundsMinX,
    boundsMinY,
    minimapWidth,
    minimapHeight,
  }) => {
    const { groupTransform, pathItems, lineItems } = useMemo(() => {
      const tx = MINIMAP_NODE_SHRINK - boundsMinX * scale;
      const ty = MINIMAP_NODE_SHRINK - boundsMinY * scale;
      const groupTransform = `translate(${tx} ${ty}) scale(${scale})`;

      const pathItems = [];
      const lineItems = [];

      (edges || []).forEach((edge, index) => {
        const pathD = edge.pathD;
        if (typeof pathD === 'string' && pathD.trim()) {
          pathItems.push({
            key: edge.id || `${edge.from}-${edge.to}-${index}`,
            d: pathD,
          });
          return;
        }

        const fromPos = nodePositions[edge.from];
        const toPos = nodePositions[edge.to];
        if (
          !fromPos ||
          !toPos ||
          !Number.isFinite(fromPos.x) ||
          !Number.isFinite(fromPos.y) ||
          !Number.isFinite(toPos.x) ||
          !Number.isFinite(toPos.y)
        ) {
          return;
        }

        const fromDims = nodeDimensions[edge.from] || { width: 290, height: 150 };
        const toDims = nodeDimensions[edge.to] || { width: 290, height: 150 };

        const x1 = fromPos.x + fromDims.width / 2;
        const y1 = fromPos.y + fromDims.height;
        const x2 = toPos.x + toDims.width / 2;
        const y2 = toPos.y;

        lineItems.push({
          key: `${edge.from}-${edge.to}-${index}`,
          x1,
          y1,
          x2,
          y2,
        });
      });

      return { groupTransform, pathItems, lineItems };
    }, [edges, nodePositions, nodeDimensions, scale, boundsMinX, boundsMinY]);

    return (
      <svg className="flowchart-minimap__edges" viewBox={`0 0 ${minimapWidth} ${minimapHeight}`}>
        <g transform={groupTransform}>
          {pathItems.map(({ key, d }) => (
            <path
              key={key}
              d={d}
              className="flowchart-minimap__edge flowchart-minimap__edge--path"
              fill="none"
            />
          ))}
          {lineItems.map(({ key, x1, y1, x2, y2 }) => (
            <line key={key} x1={x1} y1={y1} x2={x2} y2={y2} className="flowchart-minimap__edge" />
          ))}
        </g>
      </svg>
    );
  }
);

MiniEdges.displayName = 'MiniEdges';

export default MiniEdges;
